<script lang="ts">
import { computed, defineComponent, inject, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import { NavvItem } from "@/model/NavItem";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import formValidierung from "@/businesslogic/formValidierung";
import InfoBox from "@/components/grid/InfoBox.vue";
import JaNeinRadio from "@/components/JaNeinRadio.vue";
import Modal from "@/components/Modal.vue";
import OnlineKeinAngebot from "@/views/alle_strecken/modals/OnlineKeinAngebot.vue";
import GebaeudeTypRadio from "@/components/gebaeudetypen/GebaeudeTypRadio.vue";
import GebaeudetypenDaten from "@/components/gebaeudetypen/GeabeudetypenBHH";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BasisdatenBH",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    OnlineKeinAngebot,
    Modal,
    JaNeinRadio,
    InfoBox,
    ValidierungsFehler,
    Zelle,
    TitelZelle,
    Zeile,
    Ueberschrift,
    Steps,
    GebaeudeTypRadio,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const fehler = ref({} as any);
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const mindestensEinmalValidiert = ref(false);
    const eigenbauleistungUnter50ProzentVonBausumme = ref(true);

    const gebaeudetypen = ref(GebaeudetypenDaten);

    const { erzwingeGanzzahl } = formValidierung();
    const { gt } = dataUtils();

    const weiterMoeglich = computed(() => {
      return (
        piniaStore.state.antragssteller !== "architekt" &&
        piniaStore.state.bauleitungDurchDritte &&
        piniaStore.state.gebaeudedaten.eigenbauleistungen.statik !== true &&
        eigenbauleistungUnter50ProzentVonBausumme.value === true
      );
    });

    const istUmbau = computed(() => {
      return piniaStore.state.gebaeudedaten.artDesBauvorhabens == "03";
    });

    watch(
      () => piniaStore.state.gebaeudedaten.artDesBauvorhabens,
      async (artDesBauvorhabens) => {
        if (artDesBauvorhabens === "03") {
          piniaStore.state.gebaeudedaten.durchDritteBewohnt = null;
        }
      },
    );

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0 && weiterMoeglich);
        });
      });
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          piniaStore.state.neuberechnen = true;
        }
      });
      mindestensEinmalValidiert.value = true;
    };

    const maxBausumme = computed(() => {
      return piniaStore.state.isMakler ? "10000000" : "2000000";
    });

    const maxEigenbauleistungenBetrag = computed(() => {
      if (piniaStore.state.gebaeudedaten.bausumme === null) {
        return null;
      }

      return Math.floor(piniaStore.state.gebaeudedaten.bausumme * 0.5);
    });

    const erlaubteEigenbauleistungenBetragUeberschritten = computed(() => {
      return piniaStore.state.isMakler
        ? ""
        : "Bitte geben Sie die Höhe der Eigenbauleistungen in vollen € zwischen 1 und " +
            maxEigenbauleistungenBetrag.value +
            " € (50% der Bausumme) ein.\n";
    });

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 0 });
        }
      },
    );

    watch(
      () => piniaStore.state.antragssteller,
      (neu, alt) => {
        if (neu !== alt && neu === "architekt") {
          wechsleSichtbarkeit("modal-antragstellerArchitekt");
        }
      },
    );

    watch(
      () => piniaStore.state.bauleitungDurchDritte,
      (neu, alt) => {
        if (neu !== alt && !neu) {
          wechsleSichtbarkeit("modal-bauleitungDurchDritte");
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.eigenbauleistungen.erbracht,
      (neu, alt) => {
        if (neu !== alt && !neu) {
          piniaStore.state.gebaeudedaten.eigenbauleistungen.statik = false;
          piniaStore.state.gebaeudedaten.eigenbauleistungen.betrag = null;
        }
        if (neu !== alt && !neu) {
          piniaStore.state.gebaeudedaten.eigenbauleistungen.statik = false;
          piniaStore.state.gebaeudedaten.eigenbauleistungen.betrag = null;
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.eigenbauleistungen.statik,
      (neu, alt) => {
        if (neu !== alt && neu === true) {
          wechsleSichtbarkeit("modal-statik");
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.eigenbauleistungen.betrag,
      (neu, alt) => {
        if (neu !== alt && piniaStore.state.gebaeudedaten.bausumme && piniaStore.state.isMakler) {
          if (gt(neu, 0.5 * piniaStore.state.gebaeudedaten.bausumme)) {
            eigenbauleistungUnter50ProzentVonBausumme.value = false;
            wechsleSichtbarkeit("modal-eigenbauleistung");
          } else {
            eigenbauleistungUnter50ProzentVonBausumme.value = true;
          }
        }
      },
    );

    return {
      props,
      fehler,
      piniaStore,
      istSichtbar,
      istUmbau,
      wechsleSichtbarkeit,
      weiterMoeglich,
      mindestensEinmalValidiert,
      nextPage,
      erzwingeGanzzahl,
      maxBausumme,
      maxEigenbauleistungenBetrag,
      erlaubteEigenbauleistungenBetragUeberschritten,
      gebaeudetypen,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="0" />

  <section>
    <ueberschrift titel="Bauvorhaben" />

    <gebaeude-typ-radio
      v-if="piniaStore.state.isMakler"
      :fehler="fehler"
      :gebaeudetypen="gebaeudetypen"
      :required="piniaStore.state.isMakler"
    />

    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="bausummeInfo">Bausumme in €</titel-zelle>

      <zelle>
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          name="bausumme"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.bausumme"
          class="c-form-control__input"
          data-cy="bausumme"
          data-checknummer="true"
          min="0"
          :max="maxBausumme"
          required
          aria-required="true"
          aria-label="Geben Sie die Bausumme an"
        />
        <validierungs-fehler :fehler-text="fehler.bausumme" />

        <info-box id="bausummeInfo"
          >Gesamte Herstellungskosten einschließlich Mehrwertsteuer und Außenanlagen. Hinweis: Nicht zu berücksichtigen
          sind Gartenanlagen und Pflanzungen, Architektengebühren, Grundstückskosten und z.B. Baugeräte.
        </info-box>
        <p class="gev19-infotext--grey">
          {{
            piniaStore.state.isMakler
              ? "Versicherbar sind Baumaßnahmen aus dem Wohnungsbaubereich; der Wohnanteil muss mindestens 51% betragen; der gewerblich genutzte Gebäudeanteil darf höchstens 49% betragen."
              : "Unsere Bauherrenhaftpflichtversicherung gilt für Baumaßnahmen aus dem privaten Wohnungsbaubereich. Der gewerblich genutzte Gebäudeanteil darf höchstens 49 % betragen."
          }}
        </p>
      </zelle>
    </zeile>

    <zeile>
      <titel-zelle>Art des Bauvorhabens</titel-zelle>
      <zelle>
        <select
          name="artDesBauvorhabens"
          v-model="piniaStore.state.gebaeudedaten.artDesBauvorhabens"
          data-cy="artDesBauvorhabens"
          class="c-form-control-select"
          required
          aria-required="true"
          aria-label="Wählen Sie die Art des Bauvorhabens"
        >
          <option selected value="">Bitte wählen</option>
          <option value="01">Neubau</option>
          <option value="03">Umbau/Sanierung</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.artDesBauvorhabens" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <zeile v-if="istUmbau">
      <titel-zelle>Ist das Gebäude während des Umbaus durch Dritte bewohnt?</titel-zelle>
      <zelle>
        <ja-nein-radio name="durchDritteBewohnt" v-model="piniaStore.state.gebaeudedaten.durchDritteBewohnt" />
        <validierungs-fehler :fehler-text="fehler.durchDritteBewohnt" />
      </zelle>
    </zeile>

    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="wandAnWandInfo">"Wand-an-Wand" oder Lückenbebauung</titel-zelle>
      <zelle>
        <ja-nein-radio name="wandAnWand" v-model="piniaStore.state.gebaeudedaten.wandAnWand" />
        <validierungs-fehler :fehler-text="fehler.wandAnWand" />

        <info-box id="wandAnWandInfo"
          >Es wird direkt an ein bestehendes Gebäude bzw. zwischen zwei bestehende Gebäude gebaut.
          <div class="wand-an-wand">
            <img
              class="bild"
              src="@/assets/static/images/bl/wall-to-wall_tooltip.png"
              alt="Grafische Darstellung Wand an Wand Bebauung"
            />
          </div>
        </info-box>
      </zelle>
    </zeile>

    <zeile v-if="piniaStore.state.gebaeudedaten.wandAnWand">
      <titel-zelle>Gehört das bereits stehende Nachbargebäude zu Ihrem Eigentum?</titel-zelle>
      <zelle>
        <ja-nein-radio
          name="nachbargebaeudeImEigentum"
          v-model="piniaStore.state.gebaeudedaten.nachbargebaeudeImEigentum"
        />
        <validierungs-fehler :fehler-text="fehler.nachbargebaeudeImEigentum" />
      </zelle>
    </zeile>

    <ueberschrift titel="Antragsteller" />

    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="antragstellerInfo">Antragsteller ist</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.antragssteller"
          type="radio"
          id="antragsstellerBauherr"
          name="antragssteller"
          value="bauherr"
          required
          aria-required="true"
          aria-label="Antragssteller ist der Bauherr"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="antragsstellerBauherr"
        />
        <label for="antragsstellerBauherr" class="radiolabel">Bauherr</label>
        <input
          v-model="piniaStore.state.antragssteller"
          type="radio"
          id="antragsstellerArchitekt"
          name="antragssteller"
          value="architekt"
          aria-label="Antragssteller ist der Architekt, Bauträger, Bauunternehmer"
          data-cy="antragsstellerBauherr"
        />
        <label for="antragsstellerArchitekt" class="radiolabel">Architekt oder Bauträger</label>
        <validierungs-fehler :fehler-text="fehler.antragssteller" :reduzierter-abstand="true" />
        <info-box id="antragstellerInfo"
          >Nicht versicherbar sind Bauvorhaben, bei denen der Versicherungsnehmer Architekt, Bauträger, Bauunternehmer
          o. ä. ist.
        </info-box>
      </zelle>
    </zeile>

    <zeile v-if="piniaStore.state.antragssteller === 'bauherr'">
      <titel-zelle :zeige-info="true" info-box-id="bauleitungInfo"
        >Ist die Planung, Bauleitung und -ausführung an Dritte vergeben ?
      </titel-zelle>
      <zelle>
        <ja-nein-radio name="bauleitungDurchDritte" v-model="piniaStore.state.bauleitungDurchDritte" />
        <validierungs-fehler :fehler-text="fehler.bauleitungDurchDritte" />

        <info-box id="bauleitungInfo"
          >Für das Bauvorhaben muss ein externer Bauleiter oder Architekt verantwortlich sein und die Baufortschritte
          abnehmen.
        </info-box>
      </zelle>
    </zeile>

    <ueberschrift titel="Eigenbauleistung" />

    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="eigenbauleistungenInfo"
        >Werden Eigenbauleistungen erbracht?
      </titel-zelle>
      <zelle>
        <ja-nein-radio name="eigenbauleistungen" v-model="piniaStore.state.gebaeudedaten.eigenbauleistungen.erbracht" />
        <validierungs-fehler :fehler-text="fehler.eigenbauleistungen" />

        <info-box id="eigenbauleistungenInfo"
          >Voraussetzung für die Wirksamkeit dieses Versicherungsschutzes ist, dass die
          <strong>Bauarbeiten in Eigenleistungen</strong> unter der regelmäßigen Kontrolle fachlich geeigneter Personen
          wie z.B. Architekten oder selbstständigen Handwerksmeistern aus dem Baufach stehen.
        </info-box>
      </zelle>
    </zeile>

    <zeile v-if="piniaStore.state.gebaeudedaten.eigenbauleistungen.erbracht">
      <titel-zelle>Höhe der Eigenbauleistung in €</titel-zelle>
      <zelle>
        <input
          @keydown="erzwingeGanzzahl"
          name="eigenbauleistungenBetrag"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.eigenbauleistungen.betrag"
          class="c-form-control__input"
          data-cy="eigenbauleistungenBetrag"
          data-checknummer="true"
          min="1"
          :max="maxEigenbauleistungenBetrag"
          required
          aria-required="true"
          aria-label="Höhe der Eigenbauleistung in Euro"
          :data-fehlermeldung="erlaubteEigenbauleistungenBetragUeberschritten"
        />
        <validierungs-fehler :fehler-text="fehler.eigenbauleistungenBetrag" />
      </zelle>
    </zeile>

    <zeile v-if="piniaStore.state.gebaeudedaten.eigenbauleistungen.erbracht">
      <titel-zelle :zeige-info="true" info-box-id="statikInfo"
        >Werden durch Sie Arbeiten an der Statik durchgeführt?
      </titel-zelle>
      <zelle>
        <ja-nein-radio
          name="eigenbauleistungenStatik"
          v-model="piniaStore.state.gebaeudedaten.eigenbauleistungen.statik"
        />
        <validierungs-fehler :fehler-text="fehler.eigenbauleistungenStatik" />

        <info-box id="statikInfo"
          >Nicht versicherbar sind in Eigenleistung durchgeführte Arbeiten zur Erstellung, dem Abtragen oder der
          Veränderung von statisch relevanten Bauteilen wie z.B. Fundamenten inkl. Grundbruch, an tragenden Wänden,
          Decken/Böden, Fenster- und Türstürzen, Unterzügen/Überzügen und Dachkonstruktion.
        </info-box>
      </zelle>
    </zeile>

    <zeile>
      <zelle :breite="3" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          data-cy="submitZumVergleich"
          :disabled="!weiterMoeglich"
        >
          Weiter zum Vergleich
        </button>
      </zelle>
    </zeile>
  </section>

  <modal
    v-if="istSichtbar('modal-antragstellerArchitekt')"
    titel="Antragsteller ist Architekt, Bauträger, Bauunternehmer o. ä."
    @closeModal="wechsleSichtbarkeit('modal-antragstellerArchitekt')"
  >
    <template v-slot:inhalt v-if="piniaStore.state.isMakler"> Dieses Risiko ist nicht versicherbar.</template>
    <template v-slot:inhalt v-else>
      Auf Grund ihrer Angabe können wir Ihnen leider keinen Versicherungsschutz anbieten.
    </template>
  </modal>

  <modal
    v-if="istSichtbar('modal-bauleitungDurchDritte')"
    titel="Planung, Bauleitung und -ausführung"
    @closeModal="wechsleSichtbarkeit('modal-bauleitungDurchDritte')"
  >
    <template v-slot:inhalt>
      <online-kein-angebot />
    </template>
  </modal>

  <modal
    v-if="istSichtbar('modal-statik')"
    titel="Arbeiten an der Statik"
    @closeModal="wechsleSichtbarkeit('modal-statik')"
  >
    <template v-slot:inhalt>
      <online-kein-angebot />
    </template>
  </modal>

  <modal
    v-if="istSichtbar('modal-eigenbauleistung')"
    titel="Eigenbauleistung"
    @closeModal="wechsleSichtbarkeit('modal-eigenbauleistung')"
  >
    <template v-slot:inhalt>
      <strong>Höhe der Eigenbauleistung in €</strong><br />
      Dieses Risiko ist nicht versicherbar. Die Eigenbauleistung darf maximal 50% der Bausumme betragen.
    </template>
  </modal>
</template>

<style lang="scss" scoped>
.wand-an-wand {
  width: 100%;
  padding-top: 0.5rem;

  .bild {
    width: 20em;
    max-width: 100%;
    display: block;
    margin: auto;
  }
}
</style>
